import React, { useEffect, useState } from 'react';
import {
  Ship,
  FileText,
  Globe,
  Container,
  ClipboardList,
  ArrowRight,
  ShieldCheck,
  Zap,
  Search,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// ucar Landing — 중고차 수출 통합 플랫폼
//   스타일: Ponder 식 절제 톤 — near-monochrome(차콜 헤딩·버튼·로고) + 넉넉한 여백
//           + 좌 카피/우 제품 영상 비대칭 히어로. 고채도 색을 빼고 차분한 전문 톤.
// ──────────────────────────────────────────────────────────────────────────

const PROCESS = [
  { n: '1', title: '서류 업로드', desc: '자동차등록증·말소증 등 수출 서류 일괄 등록' },
  { n: '2', title: 'AI 자동 인식', desc: 'OCR 로 차량 정보 자동 추출 및 DB화' },
  { n: '3', title: '리스트 생성', desc: '컨테이너·RoRo·부품별 수출 리스트 자동 분류' },
  { n: '4', title: '서류 생성', desc: '인보이스 · 패킹리스트 원클릭 자동 생성' },
  { n: '5', title: '관세사 연동', desc: '관세사 의뢰 및 관세청 신고 자동화' },
];

const FEATURES = [
  { icon: FileText, title: '다양한 서류 지원', desc: '자동차등록증·말소증·폐차증·경락확인서 등 수출에 필요한 모든 서류 양식을 지원합니다.' },
  { icon: Zap, title: '고성능 AI OCR', desc: '최신 AI 모델로 흐릿하거나 구겨진 서류에서도 정확하게 텍스트를 추출합니다.' },
  { icon: Container, title: '운송 방식별 관리', desc: '컨테이너 선적·RoRo 선적·부품 수출 등 운송 방식에 맞춘 관리 기능을 제공합니다.' },
  { icon: ClipboardList, title: '자동 인보이스', desc: '추출 데이터를 기반으로 Commercial Invoice·Packing List 를 즉시 생성합니다.' },
  { icon: Globe, title: '관세청 연동', desc: '관세사 시스템과 연동해 신고 접수부터 수리까지 실시간으로 상태를 확인합니다.' },
  { icon: ShieldCheck, title: '데이터 검증', desc: '차대번호(VIN) 유효성 검사·중복 체크로 신고 오류를 사전에 방지합니다.' },
];

// 하단 신뢰 라인 — 도구의 핵심 연동/기반(과장 없는 사실).
const TRUST = ['Gemini AI OCR', '관세청 신고 연동', 'UCAR 레거시 실시간 동기화', 'VIN 유효성 검증'];

const LandingPage = ({ onLoginClick }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    setShown(true);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden">
      {/* ── Nav ── */}
      <nav className="fixed w-full bg-white/85 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-gray-900 inline-flex items-center justify-center shadow-sm">
                <Ship size={17} className="text-white" strokeWidth={2} />
              </span>
              <span className="font-extrabold text-xl tracking-tight lowercase">ucar</span>
            </div>
            <button
              onClick={onLoginClick}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
            >
              로그인 / 시작하기
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero (좌 카피 / 우 제품 영상) ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(1100px_460px_at_80%_-10%,#eef2fb_0%,rgba(238,242,251,0)_60%)]" />
        <div
          className={`max-w-[1360px] mx-auto px-6 lg:px-8 pt-32 pb-24 grid lg:grid-cols-[minmax(0,460px)_1fr] gap-16 items-center transition-all duration-700 ${
            shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          {/* 좌 — 카피 */}
          <div>
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-3.5 py-1.5">
              <ShieldCheck size={14} /> AI 기반 중고차 수출 통합 플랫폼
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] text-gray-900">
              복잡한 수출 업무,
              <br />
              막힘 없이 한 번에.
            </h1>
            <p className="mt-5 max-w-[480px] text-lg leading-relaxed text-gray-500">
              서류 인식부터 인보이스 생성, 관세청 신고까지 — 모든 과정을 자동화해
              중고차 수출 업무의 효율을 극대화합니다.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={onLoginClick}
                className="px-6 py-3.5 rounded-xl bg-gray-900 text-white font-bold inline-flex items-center gap-2 shadow-[0_6px_16px_-6px_rgba(20,21,26,0.3)] hover:bg-gray-800 transition-colors"
              >
                지금 시작하기 <ArrowRight size={18} />
              </button>
              <a
                href="#features"
                className="px-6 py-3.5 rounded-xl bg-white text-gray-900 font-semibold border border-gray-200 inline-flex items-center gap-2 hover:bg-gray-50 transition-colors"
              >
                <Search size={18} /> 기능 살펴보기
              </a>
            </div>
          </div>

          {/* 우 — 제품 영상 (옅은 틴트 프레임) */}
          <div className="relative">
            <div className="rounded-2xl p-3.5 bg-gradient-to-br from-[#eef2fb] to-[#f4f6fc] border border-[#e6e8f2]">
              <div className="rounded-xl overflow-hidden bg-gray-900 ring-1 ring-gray-900/10 shadow-[0_22px_50px_-22px_rgba(20,21,26,0.45)] aspect-[16/9]">
                <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                  <source src="/landing_video.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>

        {/* 신뢰 라인 */}
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pb-16">
          <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-2">
            {TRUST.map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="text-gray-300">·</span>}
                <span className="text-[13px] font-medium text-gray-400">{t}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-24 bg-[#f4f6fc]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-[13px] font-bold tracking-widest text-gray-400 uppercase">Workflow</div>
            <h2 className="mt-2.5 text-3xl font-extrabold tracking-tight text-gray-900">
              완벽하게 연결된 수출 프로세스
            </h2>
            <p className="mt-3 text-gray-500">서류 접수부터 신고까지, 끊김 없는 업무 흐름.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((p) => (
              <div
                key={p.n}
                className="bg-white p-6 rounded-2xl border border-gray-200 shadow-[0_1px_2px_rgba(20,21,26,0.04)]"
              >
                <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-base font-bold mb-4">
                  {p.n}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1.5">{p.title}</h3>
                <p className="text-sm leading-relaxed text-gray-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-[13px] font-bold tracking-widest text-gray-400 uppercase">Core Features</div>
            <h2 className="mt-2.5 text-3xl font-extrabold tracking-tight text-gray-900">
              강력한 기능으로 업무 혁신
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="bg-white p-6 rounded-2xl border border-gray-200 shadow-[0_1px_2px_rgba(20,21,26,0.04)]"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#f1effa] text-[#6a6593] flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 text-[17px] font-bold text-gray-900">{f.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-gray-500">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-white mb-3">
            지금 바로 수출 업무를 자동화하세요
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            수기 입력의 번거로움에서 벗어나 AI 자동화의 혜택을 누리세요.
          </p>
          <button
            onClick={onLoginClick}
            className="px-7 py-3.5 rounded-xl bg-white text-gray-900 font-bold inline-flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            무료로 시작하기 <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-100 text-gray-500 py-12">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-7 h-7 rounded-lg bg-gray-900 inline-flex items-center justify-center">
                  <Ship className="w-4 h-4 text-white" strokeWidth={2} />
                </span>
                <span className="font-extrabold text-lg tracking-tight lowercase text-gray-900">ucar</span>
              </div>
              <p className="text-sm leading-relaxed">
                중고차 수출 업무의 디지털 트랜스포메이션을 선도합니다.
                <br />
                빠르고 정확한 수출 신고를 위한 최고의 파트너.
              </p>
            </div>
            <div>
              <h4 className="text-gray-900 font-bold mb-3 text-sm">서비스</h4>
              <ul className="space-y-2 text-sm">
                <li>기능 소개</li>
                <li>요금 안내</li>
                <li>고객 사례</li>
                <li>API 연동</li>
              </ul>
            </div>
            <div>
              <h4 className="text-gray-900 font-bold mb-3 text-sm">고객지원</h4>
              <ul className="space-y-2 text-sm">
                <li>공지사항</li>
                <li>자주 묻는 질문</li>
                <li>문의하기</li>
                <li>이용약관</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-100 mt-10 pt-8 text-sm text-center text-gray-400">
            &copy; 2026 LOGINNETWORK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
